/**
 * Client wrapper around the generate-ai-game edge function.
 * Returns the freshly-inserted Game row so the caller can launch it
 * with the existing P5GameContainer pipeline.
 */

import { supabase } from "@/integrations/supabase/client";
import { AnalyticsEvents } from "@/lib/analytics/events";
import type { Game } from "@/types/games";
import type { Theme } from "@/components/games/dynamic/manifest";

export interface GenerateAIGamePayload {
  grade: string;
  subject?: string;
  topic?: string;
  difficulty?: "easy" | "medium" | "hard";
  theme?: Theme;
  custom_prompt?: string;
  subject_id?: string | null;
  category_id?: string | null;
  topic_id?: string | null;
}

export interface GenerateAIGameResult {
  game_id: string;
  spec: unknown;
  from_cache: boolean;
  game: Game;
}

export async function generateAIGame(
  payload: GenerateAIGamePayload,
): Promise<GenerateAIGameResult> {
  AnalyticsEvents.aiGameGenerateRequested({
    topic_id: payload.topic_id ?? undefined,
    grade: payload.grade,
    difficulty: payload.difficulty,
  });

  const { invokeAI } = await import("@/lib/aiInvoke");
  const { data, error } = await invokeAI<{ game_id: string; spec: unknown; from_cache: boolean }>(
    "generate-ai-game",
    { body: payload },
  );

  if (error) {
    const ctx = (error as any)?.context ?? {};
    const reason: any = ctx.error ?? "api_error";
    AnalyticsEvents.aiGameGenerateFailed({
      reason,
      grade: payload.grade,
    });
    throw new Error(ctx.message ?? error.message ?? "Failed to generate game");
  }

  if (!data?.game_id) {
    AnalyticsEvents.aiGameGenerateFailed({
      reason: "api_error",
      grade: payload.grade,
    });
    throw new Error("Generator returned no game id");
  }

  // Pull the full game row so the launcher gets a typed Game
  const { data: gameRow, error: gErr } = await supabase
    .from("games")
    .select("*")
    .eq("id", data.game_id)
    .maybeSingle();

  if (gErr || !gameRow) {
    throw new Error(gErr?.message ?? "Generated game not found");
  }

  AnalyticsEvents.aiGameGenerateSucceeded({
    game_id: data.game_id,
    grade: payload.grade,
    from_cache: !!data.from_cache,
  });

  return {
    game_id: data.game_id,
    spec: data.spec,
    from_cache: !!data.from_cache,
    game: gameRow as unknown as Game,
  };
}
